import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost, ApiError } from '../lib/api';

type Provider = 'gmail' | 'outlook' | 'yahoo' | 'custom';
type WizardStep = 'choose' | 'gmail-instructions' | 'app-password' | 'test-email';

interface AppPasswordForm {
  email: string;
  imapHost: string;
  imapPort: string;
  smtpHost: string;
  smtpPort: string;
  appPassword: string;
}

interface ConnectedAccount {
  id: string;
  email: string;
}

export interface EmailSetupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  /** When returning from Gmail OAuth, jump straight to test-email for this account */
  startAtTestEmail?: ConnectedAccount;
}

const PROVIDER_DEFAULTS: Record<Exclude<Provider, 'gmail'>, Pick<AppPasswordForm, 'imapHost' | 'imapPort' | 'smtpHost' | 'smtpPort'>> = {
  outlook: { imapHost: 'outlook.office365.com', imapPort: '993', smtpHost: 'smtp.office365.com', smtpPort: '587' },
  yahoo: { imapHost: 'imap.mail.yahoo.com', imapPort: '993', smtpHost: 'smtp.mail.yahoo.com', smtpPort: '465' },
  custom: { imapHost: '', imapPort: '993', smtpHost: '', smtpPort: '587' },
};

function GoogleLogo() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      <path fill="none" d="M0 0h48v48H0z" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg className="h-4 w-4 text-gray-400 ml-auto flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function ProviderInstructions({ provider }: { provider: Provider }) {
  if (provider === 'outlook') {
    return (
      <div className="rounded-md bg-blue-50 border border-blue-100 p-4 text-sm space-y-2">
        <p className="font-medium text-blue-900">How to generate an Outlook app password</p>
        <ol className="list-decimal list-inside space-y-1 text-blue-800">
          <li>Go to <strong>account.microsoft.com/security</strong></li>
          <li>Click <strong>Advanced security options</strong></li>
          <li>Under "App passwords", click <strong>Create a new app password</strong></li>
          <li>Copy the generated password and paste it below</li>
        </ol>
        <p className="text-xs text-blue-600">Requires two-step verification to be enabled on your Microsoft account.</p>
      </div>
    );
  }
  if (provider === 'yahoo') {
    return (
      <div className="rounded-md bg-purple-50 border border-purple-100 p-4 text-sm space-y-2">
        <p className="font-medium text-purple-900">How to generate a Yahoo app password</p>
        <ol className="list-decimal list-inside space-y-1 text-purple-800">
          <li>Go to <strong>login.yahoo.com/account/security</strong></li>
          <li>Scroll to "App passwords" and click <strong>Generate app password</strong></li>
          <li>Choose <strong>Other App</strong>, name it "PitchFriendly", click Generate</li>
          <li>Copy the password and paste it below</li>
        </ol>
        <p className="text-xs text-purple-600">Requires two-step verification to be enabled on your Yahoo account.</p>
      </div>
    );
  }
  return (
    <div className="rounded-md bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700">
      <p className="font-medium text-gray-900 mb-1">Custom IMAP/SMTP configuration</p>
      <p>Enter your mail provider's IMAP and SMTP server details along with an app-specific password. Check your provider's documentation for the correct server addresses and ports.</p>
    </div>
  );
}

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-colors ${i < current ? 'bg-blue-600' : i === current ? 'bg-blue-600' : 'bg-gray-200'}`}
          style={{ width: i === current ? '20px' : '8px' }}
        />
      ))}
    </div>
  );
}

export function EmailSetupWizard({ isOpen, onClose, startAtTestEmail }: EmailSetupWizardProps) {
  const queryClient = useQueryClient();

  const [step, setStep] = useState<WizardStep>(startAtTestEmail ? 'test-email' : 'choose');
  const [provider, setProvider] = useState<Provider>('outlook');
  const [connectedAccount, setConnectedAccount] = useState<ConnectedAccount | null>(startAtTestEmail ?? null);
  const [form, setForm] = useState<AppPasswordForm>({
    email: '',
    ...PROVIDER_DEFAULTS.outlook,
    appPassword: '',
  });
  const [formErrors, setFormErrors] = useState<{ imap?: string; smtp?: string } | null>(null);
  const [testEmailStatus, setTestEmailStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [testEmailError, setTestEmailError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStep(startAtTestEmail ? 'test-email' : 'choose');
      setConnectedAccount(startAtTestEmail ?? null);
      setFormErrors(null);
      setTestEmailStatus('idle');
      setTestEmailError(null);
      setProvider('outlook');
      setForm({ email: '', ...PROVIDER_DEFAULTS.outlook, appPassword: '' });
    }
  }, [isOpen, startAtTestEmail]);

  const connectGmail = useMutation({
    mutationFn: async () => {
      const res = await apiPost<{ url: string }>('/auth/google/start');
      window.location.href = res.url;
    },
  });

  const connectAppPassword = useMutation({
    mutationFn: (payload: AppPasswordForm & { provider: Provider }) =>
      apiPost<{ emailAccount: ConnectedAccount }>('/email-accounts', {
        provider: payload.provider,
        email: payload.email,
        imapHost: payload.imapHost,
        imapPort: parseInt(payload.imapPort, 10),
        smtpHost: payload.smtpHost,
        smtpPort: parseInt(payload.smtpPort, 10),
        appPassword: payload.appPassword,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
      setConnectedAccount(res.emailAccount);
      setFormErrors(null);
      setStep('test-email');
    },
    onError: (e: Error) => {
      if (e instanceof ApiError) {
        const body = e.body as { errors?: { imap?: string; smtp?: string } };
        if (body?.errors) {
          setFormErrors(body.errors);
          return;
        }
      }
      setFormErrors({ imap: e.message });
    },
  });

  const sendTestEmail = useMutation({
    mutationFn: (accountId: string) =>
      apiPost<{ ok: boolean; error?: string }>(`/email-accounts/${accountId}/send-test`),
    onSuccess: (res) => {
      if (res.ok) {
        setTestEmailStatus('sent');
      } else {
        setTestEmailStatus('error');
        setTestEmailError(res.error ?? 'Failed to send test email');
      }
    },
    onError: (e: Error) => {
      setTestEmailStatus('error');
      setTestEmailError(e.message);
    },
  });

  function handleProviderSelect(p: Provider) {
    setProvider(p);
    if (p === 'gmail') {
      setStep('gmail-instructions');
    } else {
      const defaults = PROVIDER_DEFAULTS[p];
      setForm((f) => ({ ...f, ...defaults }));
      setStep('app-password');
    }
  }

  function handleProviderChange(p: Exclude<Provider, 'gmail'>) {
    setProvider(p);
    const defaults = PROVIDER_DEFAULTS[p];
    setForm((f) => ({ ...f, ...defaults }));
    setFormErrors(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormErrors(null);
    connectAppPassword.mutate({ ...form, provider });
  }

  const stepNumber = step === 'choose' ? 0 : step === 'gmail-instructions' || step === 'app-password' ? 1 : 2;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {(step === 'gmail-instructions' || step === 'app-password') && (
              <button
                type="button"
                onClick={() => setStep('choose')}
                className="text-gray-400 hover:text-gray-600 -ml-1 p-1 rounded"
                aria-label="Back"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {step === 'choose' && 'Connect an email account'}
                {step === 'gmail-instructions' && 'Connect Gmail'}
                {step === 'app-password' && (provider === 'outlook' ? 'Connect Outlook' : provider === 'yahoo' ? 'Connect Yahoo Mail' : 'Connect email account')}
                {step === 'test-email' && 'Account connected'}
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StepIndicator current={stepNumber} total={3} />
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {/* Step 1: Choose provider */}
          {step === 'choose' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-4">
                Send emails directly from your own inbox — no "sent via" markers, no third-party sender reputation.
              </p>

              <button
                type="button"
                onClick={() => handleProviderSelect('gmail')}
                className="w-full flex items-center gap-4 rounded-lg border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 p-4 text-left transition-colors"
              >
                <GoogleLogo />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Gmail</p>
                  <p className="text-xs text-gray-500 mt-0.5">Sign in with Google — no app password needed</p>
                </div>
                <ChevronRight />
              </button>

              <button
                type="button"
                onClick={() => handleProviderSelect('outlook')}
                className="w-full flex items-center gap-4 rounded-lg border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 p-4 text-left transition-colors"
              >
                <div className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded bg-blue-500">
                  <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M2 6a2 2 0 012-2h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6zm3.519 0L12 11.671 18.481 6H5.52zM20 7.329l-7.13 5.788a1.5 1.5 0 01-1.74 0L4 7.329V18h16V7.329z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Outlook / Microsoft 365</p>
                  <p className="text-xs text-gray-500 mt-0.5">Connect with an app password</p>
                </div>
                <ChevronRight />
              </button>

              <button
                type="button"
                onClick={() => handleProviderSelect('yahoo')}
                className="w-full flex items-center gap-4 rounded-lg border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 p-4 text-left transition-colors"
              >
                <div className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded bg-purple-600">
                  <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3 5h5l4 6 4-6h5l-7 10v4h-4v-4L3 5z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Yahoo Mail</p>
                  <p className="text-xs text-gray-500 mt-0.5">Connect with an app password</p>
                </div>
                <ChevronRight />
              </button>

              <button
                type="button"
                onClick={() => handleProviderSelect('custom')}
                className="w-full flex items-center gap-4 rounded-lg border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 p-4 text-left transition-colors"
              >
                <div className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded bg-gray-400">
                  <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Other / Custom</p>
                  <p className="text-xs text-gray-500 mt-0.5">Any IMAP/SMTP provider with an app password</p>
                </div>
                <ChevronRight />
              </button>
            </div>
          )}

          {/* Step 2a: Gmail instructions */}
          {step === 'gmail-instructions' && (
            <div className="space-y-4">
              <div className="rounded-md bg-blue-50 border border-blue-100 p-4 text-sm space-y-2">
                <p className="font-medium text-blue-900">You'll be redirected to Google to authorize access.</p>
                <p className="text-blue-800">
                  PitchFriendly uses this access only to send emails on your behalf. We do not read, store, or index your existing emails.
                </p>
              </div>

              {connectGmail.isError && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                  {connectGmail.error instanceof Error ? connectGmail.error.message : 'Failed to start Google authorization'}
                </div>
              )}

              <button
                type="button"
                onClick={() => connectGmail.mutate()}
                disabled={connectGmail.isPending}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-white border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
              >
                <GoogleLogo />
                {connectGmail.isPending ? 'Redirecting to Google…' : 'Sign in with Google'}
              </button>
            </div>
          )}

          {/* Step 2b: App password form */}
          {step === 'app-password' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Provider switcher for non-Gmail */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Provider</label>
                <select
                  value={provider}
                  onChange={(e) => handleProviderChange(e.target.value as Exclude<Provider, 'gmail'>)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="outlook">Outlook / Microsoft 365</option>
                  <option value="yahoo">Yahoo Mail</option>
                  <option value="custom">Other / Custom</option>
                </select>
              </div>

              <ProviderInstructions provider={provider} />

              {formErrors && (formErrors.imap || formErrors.smtp) && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800 space-y-1">
                  {formErrors.imap && <p><strong>IMAP:</strong> {formErrors.imap}</p>}
                  {formErrors.smtp && <p><strong>SMTP:</strong> {formErrors.smtp}</p>}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email address</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="you@example.com"
                />
              </div>

              {/* Server settings — read-only for known providers, editable for custom */}
              {provider !== 'custom' ? (
                <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs text-gray-600 space-y-1">
                  <p className="font-medium text-gray-700">Server settings (pre-configured)</p>
                  <p>IMAP: {form.imapHost} : {form.imapPort}</p>
                  <p>SMTP: {form.smtpHost} : {form.smtpPort}</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">IMAP host</label>
                      <input
                        type="text"
                        required
                        value={form.imapHost}
                        onChange={(e) => setForm((f) => ({ ...f, imapHost: e.target.value }))}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="imap.example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">IMAP port</label>
                      <input
                        type="number"
                        required
                        value={form.imapPort}
                        onChange={(e) => setForm((f) => ({ ...f, imapPort: e.target.value }))}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">SMTP host</label>
                      <input
                        type="text"
                        required
                        value={form.smtpHost}
                        onChange={(e) => setForm((f) => ({ ...f, smtpHost: e.target.value }))}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="smtp.example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">SMTP port</label>
                      <input
                        type="number"
                        required
                        value={form.smtpPort}
                        onChange={(e) => setForm((f) => ({ ...f, smtpPort: e.target.value }))}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">App password</label>
                <input
                  type="password"
                  required
                  value={form.appPassword}
                  onChange={(e) => setForm((f) => ({ ...f, appPassword: e.target.value }))}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="App-specific password — not your account password"
                  autoComplete="new-password"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  type="submit"
                  disabled={connectAppPassword.isPending}
                  className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {connectAppPassword.isPending ? 'Testing connection…' : 'Connect account'}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('choose'); setFormErrors(null); }}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Back
                </button>
              </div>

              <p className="text-xs text-gray-400">
                We verify IMAP and SMTP before saving. Credentials are encrypted at rest with AES-256-GCM.
              </p>
            </form>
          )}

          {/* Step 3: Test email */}
          {step === 'test-email' && connectedAccount && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 border border-green-100">
                <div className="flex-shrink-0 h-9 w-9 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-green-900">Connected</p>
                  <p className="text-xs text-green-700">{connectedAccount.email}</p>
                </div>
              </div>

              <p className="text-sm text-gray-600">
                Optionally send a test email to yourself to confirm everything is working before you start sending pitches.
              </p>

              {testEmailStatus === 'sent' && (
                <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                  Test email sent to <strong>{connectedAccount.email}</strong>. Check your inbox!
                </div>
              )}

              {testEmailStatus === 'error' && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                  {testEmailError}
                </div>
              )}

              <div className="flex gap-2">
                {testEmailStatus !== 'sent' && (
                  <button
                    type="button"
                    onClick={() => {
                      setTestEmailStatus('sending');
                      sendTestEmail.mutate(connectedAccount.id);
                    }}
                    disabled={sendTestEmail.isPending}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {sendTestEmail.isPending ? 'Sending…' : 'Send test email'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {testEmailStatus === 'sent' ? 'Done' : 'Skip'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
